import { Button, makeStyles } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from 'lodash';
import moment from 'moment';
import EditOrderModal from "../../EditOrderModal";
import { getBranchById, getUserById, getShien, getScanSatus } from "../../../utilities/common";
import { useDispatch } from "react-redux";
import { handleEditOrderModal } from "../../../reducers/uiSlice";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
      display: "none"
    },
    "& .MuiDataGrid-window": {
      backgroundColor: theme.palette.background.paper
    }
  }
}))

function InvestigationTable(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('incidentManagement');

  const { data, statusList, userList, loading,
    branchList, openIncidentForm, tableInfo, handleSearch, onSelectionModelChange } = props;

  //MUI library data grid pagination starts at 0, but API page starts at 1
  const [page, setPage] = useState(0);
  const [selectionModel, setSelectionModel] = useState([]);

  const getNameById = (params, list) => {
    //getValue from data-grid is bugged in this current version
    return _.find(list, { 'id': params.value })?.name || "N/A"
  }

  const displayDateFromTimestamp = (timestamp, format = "YYYY-MM-DD") => {
    return _.isNil(timestamp) ? null : moment.unix(timestamp).format(format)
  }

  const columns = [
    {
      field: 'id',
      headerName: t('investigation_number_short'),
      width: 100,
      renderCell: (params) => (
        <Button
          onClick={() => openIncidentForm({ open: true, id: params.value })}
          style={{ minWidth: 100 }}
          fullWidth
        >
          {params.value}
        </Button>
      )
    },
    {
      field: 'level',
      headerName: t('incident_level_short'),
      width: 100,
      valueGetter: (params) => getNameById(params, statusList.incidentLevelList)
    },
    {
      field: 'record_time',
      headerName: t('record_time'),
      width: 140,
      valueFormatter: (params) => (displayDateFromTimestamp(params.value))
    },
    {
      field: 'tracking_no',
      headerName: t('common:tracking_number'),
      width: 200,
      renderCell: (params) => (
        <Button
          onClick={() => _handleEditOrderModal(true, params.value)}
        >
          {params.value}
        </Button>
      )
    },
    { field: 'is_shein', headerName: t('is_shein'), width: 100, valueGetter: (params) => getShien(params.value) },
    { field: 'incident_type', headerName: t('incident_type'), width: 300, valueGetter: (params) => getNameById(params, statusList.incidentTypeList) },
    { field: 'notes', headerName: t('notes'), width: 300 },
    { field: 'shipping_staff_id', headerName: t('serch_by_driver'), width: 120, valueGetter: (params) => params.value ? params.value : 'N/A' },
    { field: 'scan_status', headerName: t('scan_status'), width: 160, valueGetter: (params) => t('editOrder:' + getScanSatus(params.value)) },
    { field: 'pathInfo', headerName: t('last_tracking_path'), width: 250, valueGetter: (params) => getUserById(params.value, userList)?.username || params.value },
    { field: 'occurrence_time', headerName: t('occurrence_time'), width: 180, valueFormatter: (params) => (displayDateFromTimestamp(params.value)) },
    { field: 'location', headerName: t('current_warehouse'), width: 180, valueFormatter: (params) => getBranchById(params.value, branchList)?.name.replace("Warehouse", "").trim() || params.value },
    { field: 'destination_warehouse', headerName: t('warehouse'), width: 210, valueFormatter: (params) => getBranchById(params.value, branchList)?.name.replace("Warehouse", "").trim() || params.value },
    { field: 'status', headerName: t('investigation_status_short'), width: 100, valueGetter: (params) => getNameById(params, statusList.incidentStatusList) },
    { field: 'close_time', headerName: t('close_time'), width: 150, valueFormatter: (params) => (displayDateFromTimestamp(params.value)) },
    { field: 'source', headerName: t('start_from'), width: 175, valueGetter: (params) => getNameById(params, statusList.sourceList) },
    { field: 'recorded_by', headerName: t('recorded_by'), width: 175, valueGetter: (params) => getUserById(params.value, userList)?.username || params.value },
    { field: 'handler', headerName: t('handled_by'), width: 200, valueGetter: (params) => getUserById(params.value, userList)?.username || params.value },
    { field: 'caused_by', headerName: t('caused_by'), width: 175, valueGetter: (params) => getNameById(params, statusList.causedByList) },
    { field: 'root_cause', headerName: t('root_cause'), width: 200, valueGetter: (params) => getNameById(params, statusList.rootCauseList) },
    { field: 'result_type', headerName: t('result_type'), width: 225, valueGetter: (params) => getNameById(params, statusList.resultList) },
    { field: 'corrective_action', headerName: t('remedial_corrective_action'), width: 300, valueGetter: (params) => getNameById(params, statusList.correctiveActionList) },
    { field: 'review_type', headerName: t('review_type'), width: 250, valueGetter: (params) => getNameById(params, statusList.reviewTypeList) },
  ]

  const _handleEditOrderModal = (open, value) => {
    dispatch(handleEditOrderModal({ 
      open: open,
      orderIdentifier: value,
      type: 'tno'
    }));
  }

  return (
    <>
      <DataGrid
        className={classes.root}
        columns={columns}
        rows={data}
        pagination
        paginationMode="server"
        pageSize={tableInfo.perPage}
        page={page}
        rowsPerPageOptions={[20]}
        rowCount={tableInfo.total}
        loading={loading}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        onPageChange={(newPage) => {
          handleSearch(newPage + 1)
          setPage(newPage)
        }}
        onSelectionModelChange={(newSelectionModel) => {
          let selection = selectionModel;
          let removedId = _.difference(selection, newSelectionModel);
          let additionId = _.difference(newSelectionModel, selection);

          //Only allow 1 item, this is to prevent page change removing all selection
          removedId.length === 1 ?
            selection = _.difference(selection, removedId)
            :
            selection = _.union(selection, additionId)
          //First is to trigger other page, second is for this component.
          //They should work independent from each other
          onSelectionModelChange(selection)
          setSelectionModel(selection)
        }}
        selectionModel={selectionModel}
      />
      <EditOrderModal
        onClose={() => _handleEditOrderModal(false)}
        handleClose={() => _handleEditOrderModal(false)}
      />
    </>
  )
}

export default InvestigationTable;